import {
  AbstractViewModel,
  useViewModel,
  toArray,
  Logger,
  ChatbotFlowEngine,
  ChatbotEventBridge,
  type Draft,
  type ChatbotViewModel,
  type ChatActionEventPayload,
  type ChatMessage,
  type FlowContext,
  type FlowState,
  type FlowStepDefinition,
  type FlowStepRef,
} from '@footsteps/shared';
import {
  WozCategoriesMap,
  WozMainCategoriesMap,
  type WozCategoryCode,
  type WozMainCategoryCode,
  type WozCaseKadaster,
} from '@footsteps/types';
import { WozCaseStorageService } from '../services/storage/woz-case-storage.service';
import { WozCaseAttachmentService } from '../services/attachments/woz-case-attachment.service';
import { WozCaseEntity } from './entities';
import type { WozCaseJSON } from '../woz-case-manager.state';
import type { AddressByIdResponse } from '../services/supabase-api/supabase-api.types';
import type {
  WozCaseType,
  WozCaseStatus,
  WozCaseComplaint,
  WozCaseComplaintMode,
  WozCaseFlowState,
  WozCaseQuestion,
  WozCaseAttachment,
  WozComplaintPayload,
  WozComplaintPayloadAnswer,
  WozComplaintPayloadQuestion,
  AttachmentSource,
} from './woz-case.types';
import type { WozComplaint, WozComplaintClassificationResponse } from '../services/supabase-api';
import { SupabaseApiService } from '../services/supabase-api';
import type { WozCaseState, ActiveModal } from './woz-case.state';
import { WozFlow, FlowStepId, FlowActionId, type ActionStepCallbacks } from './woz-chatbot-flows';

export class WozCaseViewModel extends AbstractViewModel<WozCaseState> {
  static initialState = (data: WozCaseType): WozCaseState => ({
    data,
    isLoading: false,
    isSaving: false,
    isSyncing: false,
    isFlowComplete: false,
    isFormatting: false,
    hasUnformattedComplaints: false,
    canUndoAnswer: false,
    flow: { ...WozFlow.initialState },
    activeModal: null,
    shareConsent: false,
    contactConsent: false,
    attachments: [],
    isLoadingAttachments: false,
    isUploadingAttachment: false,
    deleteComplaintSequence: null,
    isSubmitting: false,
    submitIssues: [],
    submitError: null,
  });

  private readonly logger: Logger;
  readonly storageService: WozCaseStorageService;
  readonly attachmentService: WozCaseAttachmentService;
  readonly eventBridge = new ChatbotEventBridge();

  private flowEngine: ChatbotFlowEngine | null = null;
  private chatbotRef: ChatbotViewModel | null = null;
  private _dbSyncInFlight = false;
  private _dbSyncQueued = false;

  constructor(currentCase: WozCaseType) {
    super(WozCaseViewModel.initialState(currentCase));
    this.logger = new Logger(this.constructor.name);

    this.storageService = new WozCaseStorageService(currentCase.source);
    this.attachmentService = new WozCaseAttachmentService();

    this.submitCase = this.submitCase.bind(this);
    this.confirmDeleteComplaint = this.confirmDeleteComplaint.bind(this);
    this.uploadAttachment = this.uploadAttachment.bind(this);
    this.deleteAttachment = this.deleteAttachment.bind(this);
    this.updateAttachmentDescription = this.updateAttachmentDescription.bind(this);
    this.getAttachmentSignedUrl = this.getAttachmentSignedUrl.bind(this);

    this.setupEventBridge();
    this.refreshHasUnformattedComplaints();
  }

  /**
   * Override to auto-set updatedAt and persist based on source.
   */
  override update(recipe: (state: Draft<WozCaseState>) => void) {
    super.update((state) => {
      recipe(state);
    });

    super.update((state) => {
      if (state.data?.source === 'local') {
        state.data.updatedAt = new Date().toISOString();
      }
    });

    this.persist();
  }

  /** Wire the chatbot viewmodel so the case viewmodel can drive it via the flow engine. */
  setChatbot(chatbot: ChatbotViewModel) {
    this.chatbotRef = chatbot;
    this.flowEngine = new ChatbotFlowEngine(chatbot);

    // Set up the default flow steps
    this.flowEngine.setSteps(WozFlow.createDefaultSteps());

    // Wire hooks for WoZ-specific side effects
    this.flowEngine.onTriggerCompleted = (triggerId: string, data?: FlowContext) => {
      this.handleTriggerSideEffects(triggerId, data);
      this.closeModal();
    };

    this.flowEngine.onInputReceived = (text: string, step: FlowStepDefinition) => {
      this.handleOpenQuestionInput(text, step);
    };

    this.flowEngine.onContextUpdated = (context: FlowContext) => {
      this.syncContextToCaseState(context);
    };

    this.flowEngine.onStateChanged = () => {
      this.syncFlowState();
    };

    // Restore persisted flow state if available
    this.restoreFlowState();
  }

  /**
   * Subscribe to ChatbotEventBridge events emitted by chat action buttons.
   *
   * Note: these subscriptions are NOT torn down in dispose(). In React strict
   * mode (dev), useViewModel's cleanup calls dispose() between mount cycles.
   * Completing the RxJS Subject would kill the bridge permanently because the
   * viewmodel instance survives via useState. The Subject and its subscriptions
   * become collectible together with the viewmodel on real unmount.
   */
  private setupEventBridge() {
    this.eventBridge.on<ChatActionEventPayload>('openModal').subscribe((data) => {
      if (data?.payload) {
        const currentStep = this.flowEngine?.getState().currentStep;
        if (currentStep?.meta?.action) {
          const cIdx = (currentStep.meta.complaintIndex as number) ?? 0;
          const qIdx = (currentStep.meta.questionIndex as number) ?? 0;
          this.openModal({
            type: data.payload as 'select-address' | 'select-addresses',
            complaintIdx: cIdx,
            questionIdx: qIdx,
          });
        } else {
          this.openModal({ type: data.payload } as NonNullable<ActiveModal>);
        }
      }
    });

    this.eventBridge.on<ChatActionEventPayload>('trigger').subscribe((data) => {
      if (data?.payload) {
        this.completeTrigger(data.payload, {
          actionId: data.actionId,
          actionLabel: data.actionLabel,
        });
      }
    });

    this.eventBridge.on('revert').subscribe(() => {
      this.undoLastAnswer();
    });
  }

  /** Start or resume the flow from the current step. */
  startFlow() {
    this.flowEngine?.startFlow();
  }

  /**
   * Complete an external trigger (e.g. a modal confirming data).
   * Delegates to the flow engine.
   */
  completeTrigger(triggerId: string, data?: FlowContext) {
    this.flowEngine?.completeTrigger(triggerId, data);
  }

  /** Read the current flow context (accumulated data from all steps). */
  getFlowContext(): FlowContext {
    return this.flowEngine?.getContext() ?? {};
  }

  /** Handle trigger-specific side effects (e.g. updating case data, echoing messages). */
  private handleTriggerSideEffects(triggerId: string, data?: FlowContext) {
    const chatbot = this.chatbotRef;

    switch (triggerId) {
      case FlowStepId.FindAddress: {
        const addressData = data?.addressData as AddressByIdResponse | undefined;
        if (addressData) {
          this.updateAddressFields(addressData);
        }
        break;
      }

      case FlowStepId.ChooseComplaintMode: {
        const actionId = data?.actionId as string;
        const actionLabel = data?.actionLabel as string;
        const flowContext = this.flowEngine!.getContext();
        const relevantComplaints = flowContext.relevantComplaints as WozComplaint[] | undefined;
        const classification = flowContext.classification as WozComplaintClassificationResponse | undefined;

        // Echo user message with the button label
        if (actionLabel) {
          chatbot?.addUserMessage({ type: 'markdown', markdown: actionLabel });
        }

        const isSingle = actionId === FlowActionId.SingleComplaint;
        const mode: WozCaseComplaintMode = isSingle ? 'single' : 'multi';

        // Assistant confirmation
        const confirmText = isSingle
          ? 'Perfect! Ik behandel alles als één punt.'
          : 'Perfect! Ik ga nu elk punt afzonderlijk behandelen...';
        chatbot?.addAssistantMessage({ type: 'markdown', markdown: confirmText });

        // Build complaints array (with empty questions array)
        let complaints: WozCaseComplaint[];
        if (isSingle) {
          const singleText = classification?.fullComplaint
            ?? (relevantComplaints ?? []).map((c) => c.correctedText ?? c.text).join('\n');
          const allDetectedRefs = (relevantComplaints ?? []).flatMap((c) => c.detectedReferences ?? []);
          complaints = [
            {
              id: null,
              sequence: 0,
              text: singleText,
              correctedText: null,
              categoryCode: 'grief-other',
              hash: classification?.hash ?? relevantComplaints?.[0]?.hash ?? '',
              questions: [],
              formattedText: null,
              references: [],
              classificationRationale: classification?.rationale ?? null,
              detectedReferences: [...new Set(allDetectedRefs)],
            },
          ];
        } else {
          complaints = (relevantComplaints ?? []).map((complaint, i) => ({
            id: null,
            sequence: i,
            text: complaint.text,
            correctedText: complaint.correctedText ?? null,
            categoryCode: complaint.categoryCode ?? null,
            hash: complaint.hash,
            questions: [],
            formattedText: null,
            references: [],
            classificationRationale: complaint.rationale ?? null,
            detectedReferences: complaint.detectedReferences ?? [],
          }));
        }

        // Append to existing complaints (supports "more complaints" rounds)
        const existingComplaints = this.state.getValue().data?.complaints ?? [];
        // Re-index sequences across the full merged list
        complaints.forEach((complaint, index) => {
          complaint.sequence = existingComplaints.length + index;
        });
        this.updateComplaintMode(mode);
        this.updateComplaints([...existingComplaints, ...complaints]);

        this.updateClassificationData(classification, flowContext.complaints as string | undefined);

        // Store in flow context for downstream steps
        const engineState = this.flowEngine!.getState();
        engineState.context.complaintMode = mode;
        engineState.context.resolvedComplaints = complaints;

        break;
      }

      case FlowStepId.AskMoreComplaints: {
        const actionId = data?.actionId as string;
        const actionLabel = data?.actionLabel as string | undefined;

        // Echo user choice
        if (actionLabel) {
          chatbot?.addUserMessage({ type: 'markdown', markdown: actionLabel });
        }

        // Clear any dangling steps before branching
        this.flowEngine!.clearRemainingSteps();

        if (actionId === FlowActionId.MoreComplaintsYes) {
          // Reset complaint-related context for a fresh round
          const engineState = this.flowEngine!.getState();

          // Track how many complaints existed before this round so getFlowHandler
          // can index into the correct position in data.complaints when storing questions.
          const existingCaseComplaints = this.state.getValue().data?.complaints ?? [];
          engineState.context.__complaintOffset = existingCaseComplaints.length;

          delete engineState.context.complaints;
          delete engineState.context.classification;
          delete engineState.context.relevantComplaints;
          delete engineState.context.resolvedComplaints;
          delete engineState.context.complaintMode;
          delete engineState.context.currentComplaintIndex;

          // Insert describe → classify → get-flow loop again
          this.flowEngine!.insertStepsAfterCurrent([
            WozFlow.createDescribeComplaintsRepeatStep(),
            WozFlow.createClassifyComplaintsRepeatStep(),
            WozFlow.createGetFlowRepeatStep(),
          ]);
        } else {
          // No more complaints → insert flow-complete step
          this.flowEngine!.insertStepsAfterCurrent([WozFlow.createFlowCompleteStep()]);
        }
        break;
      }

      case FlowStepId.NoRelevantRetryOrFinish: {
        const actionId = data?.actionId as string;
        const actionLabel = data?.actionLabel as string | undefined;

        // Echo user choice
        if (actionLabel) {
          chatbot?.addUserMessage({ type: 'markdown', markdown: actionLabel });
        }

        // Clear dangling steps (e.g. getFlowRepeat) before branching
        this.flowEngine!.clearRemainingSteps();

        if (actionId === FlowActionId.NoRelevantRetry) {
          // User wants to try again — insert describe → classify → get-flow
          this.flowEngine!.insertStepsAfterCurrent([
            WozFlow.createDescribeComplaintsRepeatStep(),
            WozFlow.createClassifyComplaintsRepeatStep(),
            WozFlow.createGetFlowRepeatStep(),
          ]);
        } else {
          // User is done — finish the flow with previously collected complaints
          this.flowEngine!.insertStepsAfterCurrent([WozFlow.createFlowCompleteStep()]);
        }
        break;
      }

      case FlowStepId.GetFlowQuestion: {
        const currentStepRef = this.flowEngine!.getState().currentStep;
        const complaintIdx = (currentStepRef?.meta?.complaintIndex as number) ?? 0;
        const questionIdx = (currentStepRef?.meta?.questionIndex as number) ?? 0;
        const actionId = data?.actionId as string;
        const actionLabel = data?.actionLabel as string | undefined;

        // Action question type: bubble already rendered by completeAction; handle skip here
        if (currentStepRef?.meta?.action) {
          if (actionId === 'skip') {
            chatbot?.addUserMessage({ type: 'markdown', markdown: 'Overslaan' });
          }
          break;
        }

        const isMultipleSelect = data?.isMultipleSelect as boolean | undefined;

        if (!isMultipleSelect && actionLabel) {
          chatbot?.addUserMessage({ type: 'markdown', markdown: actionLabel });
        }

        const engineState = this.flowEngine!.getState();
        const answers = (engineState.context.answers ?? {}) as Record<string, string>;
        const answerKey = `${complaintIdx}-${questionIdx}`;
        answers[answerKey] = actionId;
        engineState.context.answers = answers;

        if (isMultipleSelect) {
          const selectedOptionIds = data?.selectedOptionIds as string[];
          this.setQuestionAnswer(complaintIdx, questionIdx, selectedOptionIds);
        } else {
          this.setQuestionAnswer(complaintIdx, questionIdx, actionId);
        }
        break;
      }

      default:
        break;
    }
  }

  /**
   * Handle free-text input for an open question step.
   * Stores the user's text as the answer on the complaint question.
   */
  private handleOpenQuestionInput(text: string, step: FlowStepDefinition) {
    if (step.id !== FlowStepId.GetFlowQuestion) return;

    const complaintIdx = (step.meta?.complaintIndex as number) ?? 0;
    const questionIdx = (step.meta?.questionIndex as number) ?? 0;

    // Store in flow context answers map
    const engineState = this.flowEngine!.getState();
    const answers = (engineState.context.answers ?? {}) as Record<string, string>;
    answers[`${complaintIdx}-${questionIdx}`] = text;
    engineState.context.answers = answers;

    // Store on the complaint question
    this.setQuestionAnswer(complaintIdx, questionIdx, text);
  }

  // ── Question answer tracking ──────────────────────────────────────────

  /**
   * Unified action completion — reads `onComplete` + `formatBubble` from the
   * current step's meta, so every action type is self-describing.
   */
  completeAction = (data: unknown) => {
    const stepIndex = this.flowEngine?.getState().currentStepIndex;
    const step = stepIndex != null ? this.flowEngine?.getStepAt(stepIndex) : undefined;
    if (!step?.meta) return;

    const { formatBubble, onComplete } = step.meta as Partial<ActionStepCallbacks>;

    if (formatBubble) {
      const bubble = formatBubble(data);
      if (bubble) {
        this.chatbotRef?.addUserMessage({ type: 'markdown', markdown: bubble });
      }
    }

    if (onComplete) {
      onComplete(this, data);
    }
  };

  setQuestionAnswer(complaintIdx: number, questionIdx: number, answer: WozCaseQuestion['answer']) {
    this.update((state) => {
      if (!state.data) return;
      const complaint = state.data.complaints[complaintIdx];
      if (complaint?.questions?.[questionIdx]) {
        complaint.questions[questionIdx].answer = answer;
      }
    });
  }

  /**
   * Clear the answer for a question (used during undo).
   */
  private clearQuestionAnswer(complaintIdx: number, questionIdx: number) {
    this.update((state) => {
      if (!state.data) return;
      const complaint = state.data.complaints[complaintIdx];
      if (complaint?.questions?.[questionIdx]) {
        complaint.questions[questionIdx].answer = null;
      }
    });
  }

  /**
   * Store fetched questions on a complaint.
   * Called from getFlowHandler via the viewmodel.
   */
  updateComplaintQuestions(complaintIndex: number, questions: WozCaseQuestion[]) {
    this.update((state) => {
      if (!state.data) return;
      const complaint = state.data.complaints[complaintIndex];
      if (complaint) {
        complaint.questions = questions;
      }
    });
  }

  /** Sync specific flow context keys to persisted case data. */
  private syncContextToCaseState(context: FlowContext) {
    if ('addressData' in context) {
      const addressData = context.addressData as AddressByIdResponse | undefined;
      if (addressData) {
        this.updateAddressFields(addressData);
        this.updateKadaster(addressData.kadaster);
      }
    }

    // Auto-set complaintMode + append complaint for single-complaint path.
    // (ChooseComplaintMode is only shown for 2+ complaints, so single complaints
    // would otherwise never populate the case's complaints array.)
    if ('relevantComplaints' in context) {
      const relevant = context.relevantComplaints as WozComplaint[] | undefined;
      const classification = context.classification as WozComplaintClassificationResponse | undefined;
      if (relevant?.length === 1) {
        const complaint = relevant[0];
        const existing = this.state.getValue().data?.complaints ?? [];
        const newComplaint: WozCaseComplaint = {
          id: null,
          sequence: existing.length,
          text: complaint.text,
          correctedText: complaint.correctedText ?? null,
          categoryCode: complaint.categoryCode ?? null,
          hash: complaint.hash,
          questions: [],
          formattedText: null,
          references: [],
          classificationRationale: complaint.rationale ?? null,
          detectedReferences: complaint.detectedReferences ?? [],
        };

        // Append to existing complaints (supports "more complaints" rounds)
        this.updateComplaints([...existing, newComplaint]);

        // Set mode on first round only (don't override a previous multi choice)
        if (!this.state.getValue().data?.complaintMode) {
          this.updateComplaintMode('single');
        }

        const fullContext = this.flowEngine?.getContext() ?? {};
        this.updateClassificationData(classification, fullContext.complaints as string | undefined);
      }
    }

    // Store fetched questions on the complaint (signalled by getFlowHandler)
    if (context.__updateComplaintQuestions) {
      const { complaintIndex, questions } = context.__updateComplaintQuestions as {
        complaintIndex: number;
        questions: WozCaseQuestion[];
      };
      this.updateComplaintQuestions(complaintIndex, questions);
      // Clean up the transient signal from context
      delete context.__updateComplaintQuestions;
    }
  }

  updateAddressFields(addressData: Partial<AddressByIdResponse>) {
    this.update((state) => {
      if (!state.data) return;
      if (addressData.addressPointId !== undefined) state.data.addressPointId = addressData.addressPointId;
      if (addressData.addressCity !== undefined) state.data.addressCity = addressData.addressCity;
      if (addressData.addressStreet !== undefined) state.data.addressStreet = addressData.addressStreet;
      if (addressData.addressHouseNumber !== undefined) state.data.addressHouseNumber = addressData.addressHouseNumber;
      if (addressData.addressHouseLetter !== undefined) state.data.addressHouseLetter = addressData.addressHouseLetter;
      if (addressData.addressHouseNumberAddition !== undefined)
        state.data.addressHouseNumberAddition = addressData.addressHouseNumberAddition;
      if (addressData.addressPostalCode !== undefined) state.data.addressPostalCode = addressData.addressPostalCode;
    });
  }

  updateKadaster(kadaster: WozCaseKadaster | null) {
    this.update((state) => {
      if (state.data) {
        state.data.kadaster = kadaster;
      }
    });
  }

  /**
   * Recompute hasUnformattedComplaints from current complaints and push to state.
   */
  private refreshHasUnformattedComplaints(): void {
    const complaints = this.state.getValue().data?.complaints ?? [];
    const hasUnformatted = complaints.some((c) => c.formattedText === null);
    super.update((state) => {
      state.hasUnformattedComplaints = hasUnformatted;
    });
  }

  /**
   * Call the format-complaints API for complaints that have not yet been formatted.
   * Sets `isFormatting` while in progress and stores the formatted text on each complaint by id.
   */
  async formatComplaints(): Promise<void> {
    if (this.state.getValue().isFormatting) return;

    const payload = this.prepareComplaintsPayload();

    // Only send complaints that still need formatting
    const unformatted = payload.filter((p) => {
      const complaint = (this.state.getValue().data?.complaints ?? []).find((c) => c.sequence === p.sequence);
      return complaint?.formattedText === null;
    });

    if (unformatted.length === 0) {
      // No actionable complaints — sync state flag to prevent re-trigger
      this.refreshHasUnformattedComplaints();
      return;
    }

    super.update((state) => {
      state.isFormatting = true;
    });

    try {
      const api = SupabaseApiService.getInstance();
      const results = await api.formatComplaints(unformatted);

      this.update((state) => {
        if (!state.data) return;
        for (const result of results) {
          const complaint = state.data.complaints.find((c) => c.sequence === result.sequence);
          if (complaint) {
            complaint.formattedText = result.formattedText;
            complaint.references = result.references.map((ref) => ({
              id: null,
              ...ref,
              metadata: ref.metadata ?? null,
            }));
          }
        }
      });

      // If the flow already completed before formatting finished,
      // sync the formatted text to the database.
      if (this.state.getValue().isFlowComplete) {
        this.persistToDatabase();
      }
    } catch (error) {
      this.logger.error('formatComplaints failed', error);
    } finally {
      super.update((state) => {
        state.isFormatting = false;
      });
      this.refreshHasUnformattedComplaints();
    }
  }

  updateComplaintMode(mode: WozCaseComplaintMode | null) {
    this.update((state) => {
      if (state.data) {
        state.data.complaintMode = mode;
      }
    });
  }

  updateComplaints(complaints: WozCaseComplaint[]) {
    this.update((state) => {
      if (state.data) {
        state.data.complaints = complaints;
      }
    });
    this.refreshHasUnformattedComplaints();
  }

  private updateClassificationData(classification: WozComplaintClassificationResponse | undefined, userInput: string | undefined) {
    if (!classification) return;
    this.update((state) => {
      if (!state.data) return;

      const rationale = classification.rationale ?? null;
      const rawText = userInput ?? null;
      const formattedText = classification.fullComplaint ?? null;

      state.data.classificationRationale = state.data.classificationRationale && rationale
        ? `${state.data.classificationRationale} ${rationale}` : rationale;
      state.data.originalComplaint = state.data.originalComplaint && rawText
        ? `${state.data.originalComplaint} ${rawText}` : rawText;
      state.data.originalComplaintFormatted = state.data.originalComplaintFormatted && formattedText
        ? `${state.data.originalComplaintFormatted} ${formattedText}` : formattedText;
    });
  }

  /**
   * Prepare the complaints payload for an API call.
   *
   * - Filters out complaints with no answered questions.
   * - For `open` questions the answer is the raw text string.
   * - For `single-select` questions the answer is a resolved `{ id, caption }` object.
   * - For `multiple-select` questions the answer is an array of `{ id, caption }` objects.
   */
  prepareComplaintsPayload(): WozComplaintPayload[] {
    const complaints = this.state.getValue().data?.complaints ?? [];

    return complaints
      .filter((complaint) => complaint.formattedText === null)
      .map((complaint) => {
        const answeredQuestions: WozComplaintPayloadQuestion[] = complaint.questions
          .filter((question) => question.answer !== null)
          .map((question) => {
            if (question.type === 'open') {
              return {
                id: question.id,
                answer: question.answer as string,
              };
            }

            if (question.type === 'action') {
              let json: string | null = null;
              if (question.answer != null) {
                json = typeof question.answer === 'string' ? question.answer : JSON.stringify(question.answer);
              }
              return { id: question.id, answer: json } as WozComplaintPayloadQuestion;
            }

            const answerIds = toArray(question.answer);

            const resolved: WozComplaintPayloadAnswer[] = answerIds
              .map((id) => {
                const option = question.options.find((option) => option.id === id);
                return option ? { id: option.id } : null;
              })
              .filter((answer): answer is WozComplaintPayloadAnswer => answer !== null);

            return {
              id: question.id,
              answer: question.type === 'multiple-select' ? resolved : resolved[0],
            } as WozComplaintPayloadQuestion;
          });

        if (complaint.questions.length > 0 && answeredQuestions.length === 0) return null;

        return {
          sequence: complaint.sequence,
          text: complaint.text,
          correctedText: complaint.correctedText,
          categoryCode: complaint.categoryCode,
          hash: complaint.hash,
          questions: answeredQuestions,
        } as WozComplaintPayload;
      })
      .filter((complaint): complaint is WozComplaintPayload => complaint !== null);
  }

  /** Update the messages array on the case (used by WozCaseChatbotStorageAdapter). */
  updateMessages(messages: ChatMessage[]) {
    // Use super.update to avoid the auto-persist cycle (the chatbot viewmodel
    // already triggers persist via its own update → storage.save → this method).
    super.update((state) => {
      if (state.data) {
        state.data.messages = messages;
      }
    });
  }

  /** Open a modal by structured descriptor. */
  openModal(modal: NonNullable<ActiveModal>) {
    super.update((state) => {
      state.activeModal = modal;
    });
  }

  /** Close the currently open modal. */
  closeModal() {
    super.update((state) => {
      state.activeModal = null;
    });
  }

  updateStatus(status: WozCaseStatus) {
    this.update((state) => {
      if (state.data) {
        state.data.status = status;
      }
    });
  }

  async submitCase(): Promise<boolean> {
    const issues = this.getSubmitIssues();
    if (issues.length) {
      this.setSubmitIssues(issues);
      return false;
    }

    const { data, shareConsent, contactConsent } = this.state.getValue();
    if (!data?.id) {
      this.logger.warn('submitCase blocked: no case ID');
      return false;
    }

    super.update((state) => {
      state.isSubmitting = true;
    });

    try {
      const api = SupabaseApiService.getInstance();
      await api.submitCase(data.id, { shareConsent, contactConsent });
      this.updateStatus('submitted');
      this.storageService.clear();
      return true;
    } catch (error) {
      this.logger.error('submitCase failed', error);
      const message = error instanceof Error ? error.message : 'Er is een onbekende fout opgetreden.';
      this.setSubmitError(message);
      return false;
    } finally {
      super.update((state) => {
        state.isSubmitting = false;
      });
    }
  }

  setSubmitError(error: string | null) {
    super.update((state) => {
      state.submitError = error;
    });
  }

  /**
   * Store a signature image (base64) and the date it was signed.
   * Immediately triggers a database persist because signing is a meaningful milestone.
   */
  updateSignature(signatureData: string, dateSigned: string) {
    this.update((state) => {
      if (state.data) {
        state.data.signatureData = signatureData;
        state.data.dateSigned = dateSigned;
      }
    });
    this.persistToDatabase();
  }

  setDeleteComplaintSequence(sequence: number | null) {
    super.update((state) => {
      state.deleteComplaintSequence = sequence;
    });
  }

  confirmDeleteComplaint() {
    const { deleteComplaintSequence } = this.state.getValue();
    if (deleteComplaintSequence !== null) {
      this.deleteComplaint(deleteComplaintSequence);
      this.setDeleteComplaintSequence(null);
    }
  }

  async deleteComplaint(sequence: number): Promise<void> {
    const { data } = this.state.getValue();
    if (!data) return;

    const complaint = data.complaints.find((c) => c.sequence === sequence);
    const dbComplaintId = complaint?.id ?? null;

    this.update((state) => {
      if (state.data) {
        state.data.complaints = state.data.complaints.filter((c) => c.sequence !== sequence);
        state.data.complaints.forEach((c, i) => {
          c.sequence = i;
        });
      }
    });
    this.refreshHasUnformattedComplaints();

    if (data.id && data.source === 'database' && dbComplaintId) {
      try {
        const api = SupabaseApiService.getInstance();
        await api.deleteComplaint(data.id, dbComplaintId);
      } catch (error) {
        this.logger.error('Failed to delete complaint from database', error);
      }
    }
  }

  clearSignature() {
    this.update((state) => {
      if (state.data) {
        state.data.signatureData = null;
        state.data.dateSigned = null;
      }
    });
    this.persistToDatabase();
  }

  setDateSigned(dateSigned: string) {
    super.update((state) => {
      if (state.data) {
        state.data.dateSigned = dateSigned;
      }
    });
  }

  setShareConsent(value: boolean) {
    super.update((state) => {
      state.shareConsent = value;
    });
  }

  setContactConsent(value: boolean) {
    super.update((state) => {
      state.contactConsent = value;
    });
  }

  getSubmitIssues(): string[] {
    const { data, shareConsent, contactConsent } = this.state.getValue();
    const issues: string[] = [];
    if (!data?.complaints?.length) issues.push('Voeg minimaal één klacht toe.');
    if (!data?.signatureData) issues.push('Plaats je handtekening.');
    if (!data?.dateSigned) issues.push('Vul de datum van ondertekening in.');
    if (!shareConsent) issues.push('Geef toestemming om het bezwaar te delen met de gemeente.');
    if (!contactConsent) issues.push('Geef toestemming om gecontacteerd te worden.');
    return issues;
  }

  setSubmitIssues(issues: string[]) {
    super.update((state) => {
      state.submitIssues = issues;
    });
  }

  // ── Attachments ────────────────────────────────────────────────

  async loadAttachments(): Promise<void> {
    const { data, isLoadingAttachments } = this.state.getValue();
    if (!data?.id || isLoadingAttachments) return;

    this.update((state) => {
      state.isLoadingAttachments = true;
    });
    const caseId = data.id;
    try {
      const attachments = await this.attachmentService.list(caseId);
      super.update((state) => {
        state.attachments = attachments;
        state.isLoadingAttachments = false;
      });
    } catch (error) {
      this.logger.error('loadAttachments failed', error);
      super.update((state) => {
        state.isLoadingAttachments = false;
      });
    }
  }

  async uploadAttachment(
    file: File,
    source: AttachmentSource,
    options?: { caseComplaintId?: string; caseComplaintQuestionId?: string; description?: string }
  ): Promise<void> {
    const caseId = this.state.getValue().data?.id;
    if (!caseId) return;

    super.update((state) => {
      state.isUploadingAttachment = true;
    });
    try {
      const attachment = await this.attachmentService.upload(file, caseId, source, options);
      super.update((state) => {
        state.attachments.push(attachment);
        state.isUploadingAttachment = false;
      });
    } catch (error) {
      this.logger.error('uploadAttachment failed', error);
      super.update((state) => {
        state.isUploadingAttachment = false;
      });
      throw error;
    }
  }

  async deleteAttachment(attachmentId: string): Promise<void> {
    try {
      await this.attachmentService.delete(attachmentId);
      super.update((state) => {
        state.attachments = state.attachments.filter((a) => a.id !== attachmentId);
      });
    } catch (error) {
      this.logger.error('deleteAttachment failed', error);
      throw error;
    }
  }

  async updateAttachmentDescription(attachmentId: string, description: string): Promise<void> {
    super.update((state) => {
      const attachment = state.attachments.find((a) => a.id === attachmentId);
      if (attachment) attachment.description = description;
    });

    try {
      await this.attachmentService.updateDescription(attachmentId, description || null);
    } catch (error) {
      this.logger.error('updateAttachmentDescription failed', error);
    }
  }

  async getAttachmentSignedUrl(attachmentId: string): Promise<string> {
    return this.attachmentService.getSignedUrl(attachmentId);
  }

  getAttachmentsForSource(source: AttachmentSource, parentId?: string): WozCaseAttachment[] {
    const { attachments } = this.state.getValue();
    return attachments.filter((a) => {
      if (a.source !== source) return false;
      if (source === 'complaint' && parentId) return a.caseComplaintId === parentId;
      if (source === 'question' && parentId) return a.caseComplaintQuestionId === parentId;
      return true;
    });
  }

  /** Reset mutable case fields back to their initial state. */
  resetData() {
    this.update((state) => {
      if (state.data) {
        state.data.addressPointId = null;
        state.data.addressCity = null;
        state.data.addressStreet = null;
        state.data.addressHouseNumber = null;
        state.data.addressHouseLetter = null;
        state.data.addressHouseNumberAddition = null;
        state.data.addressPostalCode = null;
        state.data.kadaster = null;
        state.data.complaintMode = null;
        state.data.complaints = [];
        state.data.messages = [];
        state.data.flow = null;
        state.data.status = 'open';
      }
      state.hasUnformattedComplaints = false;
    });
  }

  /** Switch from local-only to database storage and persist. Called on anon→verified transition. */
  async promoteToDatabase(): Promise<void> {
    super.update((state) => {
      if (state.data) {
        state.data.source = 'database';
      }
    });
    this.storageService.switchAdapter('database');
    return this.persistToDatabase();
  }

  reset() {
    this.resetData();
    this.flowEngine?.reset(WozFlow.createDefaultSteps());
    this.syncFlowState();
  }

  /**
   * Reopen a completed flow so the user can add more complaints.
   * Uses the same mechanism as the "more complaints → yes" path:
   * removes the flow-complete marker, resets complaint context,
   * inserts describe → classify → get-flow steps, and resumes.
   */
  reopenFlow() {
    if (!this.flowEngine) return;

    // Remove flow-complete from completed steps so isFlowComplete becomes false
    this.flowEngine.removeCompletedStep(FlowStepId.FlowComplete);

    // Reset complaint-related context for a fresh round (same as MoreComplaintsYes)
    const engineState = this.flowEngine.getState();

    // Track how many complaints existed before this round so getFlowHandler
    // can index into the correct position in data.complaints when storing questions.
    const existingComplaints = this.state.getValue().data?.complaints ?? [];
    engineState.context.__complaintOffset = existingComplaints.length;

    delete engineState.context.complaints;
    delete engineState.context.classification;
    delete engineState.context.relevantComplaints;
    delete engineState.context.resolvedComplaints;
    delete engineState.context.complaintMode;
    delete engineState.context.currentComplaintIndex;

    // Insert steps after the current position (same as MoreComplaintsYes)
    this.flowEngine.insertStepsAfterCurrent([
      WozFlow.createDescribeComplaintsRepeatStep(),
      WozFlow.createClassifyComplaintsRepeatStep(),
      WozFlow.createGetFlowRepeatStep(),
    ]);

    // Sync state (marks isFlowComplete = false).
    this.syncFlowState();
    this.flowEngine.startFlow();
  }

  /** Undo the last answered question (revert one step back). */
  undoLastAnswer(): boolean {
    if (!this.flowEngine) return false;

    const state = this.flowEngine.getState();
    const prevIndex = state.currentStepIndex - 1;
    const prevStep = this.flowEngine.getSteps()[prevIndex];

    if (!this.flowEngine.revertToPreviousStep()) return false;

    // Remove answer from context and from the complaint question
    if (prevStep) {
      const meta = prevStep.meta;
      if (prevStep.id === FlowStepId.GetFlowQuestion && meta) {
        const complaintIdx = (meta.complaintIndex as number) ?? 0;
        const questionIdx = (meta.questionIndex as number) ?? 0;

        // Remove from context answers map
        const answers = (state.context.answers ?? {}) as Record<string, string>;
        delete answers[`${complaintIdx}-${questionIdx}`];

        // Clear the answer on the complaint question
        this.clearQuestionAnswer(complaintIdx, questionIdx);
      }
    }

    this.syncFlowState();
    return true;
  }

  // ── Persistence ────────────────────────────────────────────────────────

  /** Always writes to localStorage (fast, synchronous). */
  private persist() {
    const { data } = this.state.getValue();
    if (!data) return;
    this.storageService.save(new WozCaseEntity(data));
  }

  /**
   * Persist the current case to the database.
   * If no data.id → creates a new case, stores the DB-assigned id.
   * If data.id exists → updates the existing case.
   * Called on flow completion and anon→verified transition.
   */
  async persistToDatabase(): Promise<void> {
    const { data } = this.state.getValue();
    if (!data || data.source === 'local') return;

    if (this._dbSyncInFlight) {
      this._dbSyncQueued = true;
      return;
    }

    this._dbSyncInFlight = true;
    this._dbSyncQueued = false;
    super.update((state) => {
      state.isSyncing = true;
    });

    let success = false;
    try {
      const entity = new WozCaseEntity(data);
      const saved = await this.storageService.saveToDatabase(entity);

      super.update((state) => {
        if (state.data) {
          state.data.id = saved.id;
          state.data.source = 'database';
          for (const savedComplaint of saved.complaints) {
            const local = state.data.complaints.find((c) => c.sequence === savedComplaint.sequence);
            if (local) {
              local.id = savedComplaint.id;
              this.logger.debug(`complaint seq=${savedComplaint.sequence} → id=${savedComplaint.id}`);
            }
          }
        }
      });

      // Persist to localStorage with the DB-assigned IDs
      const updatedEntity = new WozCaseEntity(this.state.getValue().data!);
      await this.storageService.save(updatedEntity);
      success = true;
    } catch (error) {
      this._dbSyncQueued = false;
      this.logger.error('Failed to persist to database', error);
    } finally {
      this._dbSyncInFlight = false;
      super.update((state) => {
        state.isSyncing = false;
      });
    }

    if (success && this._dbSyncQueued) {
      this._dbSyncQueued = false;
      return this.persistToDatabase();
    }
  }

  /**
   * Write minimal flow state into the case data (persisted via the case entity).
   * Stores FlowStepRef[] for steps and completed steps — no step definitions, no context.
   */
  private persistFlowState() {
    if (!this.flowEngine) return;

    const engineState = this.flowEngine.getState();
    const steps = this.flowEngine.getSteps();

    const flowState: WozCaseFlowState = {
      steps: steps.map((step): FlowStepRef => {
        const ref: FlowStepRef = { id: step.id, sequence: step.sequence ?? 0 };
        if (step.meta) ref.meta = step.meta;
        return ref;
      }),
      currentStepIndex: engineState.currentStepIndex,
      completedSteps: engineState.completedSteps.map((step) => ({ ...step })),
      isWaitingForTrigger: engineState.isWaitingForTrigger,
      isWaitingForInput: engineState.isWaitingForInput,
    };

    // Write directly to state (bypass the auto-updatedAt cycle for flow-only changes)
    super.update((state) => {
      if (state.data) {
        state.data.flow = flowState;
      }
    });

    this.persist();
  }

  /**
   * Restore flow state from the case data.
   * Rebuilds full step definitions from stored FlowStepRefs + complaint data,
   * and reconstructs the flow context from case data.
   */
  private restoreFlowState() {
    if (!this.flowEngine) return;

    const { data } = this.state.getValue();
    if (!data?.flow) return;

    try {
      const { flow } = data;

      // Rebuild full step definitions from refs + complaint data
      const steps = WozFlow.rehydrateFromRefs(flow.steps, data.complaints);
      this.flowEngine.setSteps(steps);

      // Reconstruct the flow context from case data
      const context = this.buildFlowContext(data);

      // Restore the engine state
      const engineState: FlowState = {
        currentStepIndex: flow.currentStepIndex,
        currentStep: steps[flow.currentStepIndex]
          ? {
              id: steps[flow.currentStepIndex].id,
              sequence: steps[flow.currentStepIndex].sequence ?? 0,
              meta: steps[flow.currentStepIndex].meta,
            }
          : null,
        completedSteps: flow.completedSteps.map((step) => ({ ...step })),
        context,
        isWaitingForTrigger: flow.isWaitingForTrigger,
        isWaitingForInput: flow.isWaitingForInput,
      };

      this.flowEngine.setState(engineState);
      this.syncFlowState();
    } catch (error) {
      this.logger.error('Failed to restore flow state', error);
    }
  }

  /**
   * Reconstruct the FlowContext from persisted case data.
   * This replaces storing the context in localStorage directly.
   */
  private buildFlowContext(data: WozCaseType): FlowContext {
    const context: FlowContext = {};

    if (data.addressPointId) {
      context.addressData = {
        addressPointId: data.addressPointId,
        addressCity: data.addressCity,
        addressStreet: data.addressStreet,
        addressHouseNumber: data.addressHouseNumber,
        addressHouseLetter: data.addressHouseLetter,
        addressHouseNumberAddition: data.addressHouseNumberAddition,
        addressPostalCode: data.addressPostalCode,
        kadaster: data.kadaster,
      };

      const fetchAddressCompleted = (data.flow?.completedSteps ?? []).some((s) => s.id === FlowStepId.FetchAddress);
      if (fetchAddressCompleted) {
        context.__kadasterFetched = true;
      }
    }

    if (data.complaintMode) context.complaintMode = data.complaintMode;
    if (data.complaints.length > 0) {
      context.resolvedComplaints = data.complaints;
      context.relevantComplaints = data.complaints;

      // Derive currentComplaintIndex from the number of completed get-flow steps.
      const completedGetFlowCount = (data.flow?.completedSteps ?? []).filter(
        (step) => step.id === FlowStepId.GetFlow
      ).length;
      context.currentComplaintIndex = completedGetFlowCount;

      // Rebuild the answers map from complaint questions.
      // Keyed by `{complaintIndex}-{questionIndex}`.
      const answers: Record<string, string> = {};
      data.complaints.forEach((complaint, cIdx) => {
        complaint.questions.forEach((question, qIdx) => {
          if (question.answer) {
            const key = `${cIdx}-${qIdx}`;
            const ans = question.answer;
            if (typeof ans === 'string') {
              answers[key] = ans;
            } else if (Array.isArray(ans)) {
              answers[key] = ans.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(',');
            } else if (ans != null) {
              answers[key] = JSON.stringify(ans);
            }
          }
        });
      });
      if (Object.keys(answers).length > 0) {
        context.answers = answers;
      }
    }

    return context;
  }

  /** Push the internal flow state into the reactive state and persist. */
  private syncFlowState() {
    if (!this.flowEngine) return;

    const engineState = this.flowEngine.getState();
    const isFlowComplete = engineState.completedSteps.some((step) => step.id === FlowStepId.FlowComplete);

    // Determine if the user can undo the last answered question:
    // currently on a revertable step AND the previous step is also revertable.
    const steps = this.flowEngine.getSteps();
    const currentStep = steps[engineState.currentStepIndex];
    const prevStep = steps[engineState.currentStepIndex - 1];
    const canUndoAnswer =
      engineState.isWaitingForTrigger !== null && !!currentStep?.revertable && !!prevStep?.revertable;

    // Keep the chatbot messages' canRevert flag in sync
    this.chatbotRef?.setCanRevert(canUndoAnswer);

    const wasFlowComplete = this.state.getValue().isFlowComplete;

    // Deep-clone so Immer freezes the copy, not the engine's internal state.
    super.update((state) => {
      state.isFlowComplete = isFlowComplete;
      state.canUndoAnswer = canUndoAnswer;
      state.flow = {
        ...engineState,
        completedSteps: engineState.completedSteps.map((step) => ({ ...step })),
        context: structuredClone(engineState.context),
      };
    });
    this.persistFlowState();

    // Persist to database once when the flow first completes
    if (isFlowComplete && !wasFlowComplete) {
      this.persistToDatabase();
    }
  }

  getGroupedComplaints(): ComplaintMainCatGroup[] {
    const complaints = this.state.getValue().data?.complaints ?? [];

    const grouped = complaints.reduce<Map<string, ComplaintMainCatGroup>>((acc, complaint) => {
      const cat = complaint.categoryCode
        ? (WozCategoriesMap[complaint.categoryCode as WozCategoryCode] ?? null)
        : null;
      const mainCatCode = (cat && 'category' in cat && cat.category in WozMainCategoriesMap
        ? cat.category : cat?.mainCategory ?? 'other') as WozMainCategoryCode;
      const mainCat = WozMainCategoriesMap[mainCatCode] ?? WozMainCategoriesMap['other'];

      const bucket = acc.get(mainCatCode) ?? { code: mainCatCode, title: mainCat.title, priority: mainCat.priority, complaints: [] };
      if (!acc.has(mainCatCode)) acc.set(mainCatCode, bucket);
      bucket.complaints.push(complaint);

      return acc;
    }, new Map());

    return [...grouped.values()].sort((a, b) => a.priority - b.priority);
  }
}

export type ComplaintMainCatGroup = {
  code: string;
  title: string;
  priority: number;
  complaints: WozCaseComplaint[];
};

export const useWozCaseViewModel = (currentCase: WozCaseType | WozCaseJSON) =>
  useViewModel<WozCaseState, WozCaseViewModel>(() => new WozCaseViewModel(currentCase as WozCaseType));
